import React, { useCallback, useRef, useState, type PropsWithChildren } from "react";
import { type INoteObservable } from "@/store/note/types";
import { actions } from "@/actions";
import { useAppStore } from "@/store/utils/hooks";
import { useLiveQuery } from "dexie-react-hooks";
import { observer } from "mobx-react-lite";
import { EventContext } from "@/domains/metrics/context";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { orderBy } from "lodash-es";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSCollectionIcon,
  MDSMenuInputHeader,
  MDSMenuItemDivider,
  MDSIcon,
  MDSIconButton,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemSelectionVariant,
  MDSSelectMenuItem,
  MDSMenuItemHeader,
  MDSSelectMenu,
  MDSButtonSize,
} from "@/mds";
import styled from "@emotion/styled";
import { getShortcutMenuItemRightAddon } from "@/modules/keyboard-shortcuts/menu";

interface OrganizeProps extends PropsWithChildren {
  note: INoteObservable | TemplateObservable;
  showAutoOrganize?: boolean;
  autoOrganizeDisabledReason?: string;
  organizeIsOpen?: boolean;
  onOrganizeIsOpenChange?: (isOpen: boolean) => void;
}

export const OrganizeNote = observer(
  ({
    note,
    showAutoOrganize = false,
    autoOrganizeDisabledReason: controlledAutoOrganizeDisabledReason,
    organizeIsOpen: controlledOrganizeIsOpen,
    onOrganizeIsOpenChange: controlledOnOrganizeIsOpenChange,
    children,
  }: OrganizeProps) => {
    const { store } = useAppStore();
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const [internalOrganizeIsOpen, setInternalOrganizeIsOpen] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const organizeIsOpen = controlledOrganizeIsOpen ?? internalOrganizeIsOpen;
    const setOrganizeIsOpen = controlledOnOrganizeIsOpenChange ?? setInternalOrganizeIsOpen;

    const [organizeSearchText, setOrganizeSearchText] = useState("");
    const searchSuggestions =
      useLiveQuery(() => store.search.forSuggestions(organizeSearchText), [organizeSearchText]) ??
      [];

    const [showCreateCollection, setShowCreateCollection] = useState(false);
    const collections =
      organizeSearchText.length === 0
        ? store.recentItems.sortedRecentCollectionsInteractedWithByMe
        : (searchSuggestions
            .map(result => store.collections.get(result.modelId))
            .filter(Boolean) as CollectionObservable[]);
    const sortedCollections = orderBy(
      collections,
      collection => note.collectionList?.hasCollection({ collectionId: collection.id }),
      "desc"
    );

    const organizeCollectionsHeader =
      sortedCollections.length === 0
        ? null
        : organizeSearchText.trim().length > 0
          ? "Collections"
          : "Recent collections";

    const [isFiltering, setIsFiltering] = useState(false);
    const noteId = note?.id;
    const autoOrganizeDisabledReason =
      !store.floatingAgent.isMemAgentAccessEnabled
        ? "Mem Agent is not available"
        : note instanceof TemplateObservable
        ? "Only available for notes"
        : !noteId
          ? "Save the note before auto organizing"
          : controlledAutoOrganizeDisabledReason;
    const canAutoOrganize = !autoOrganizeDisabledReason;

    const handleSearchCollections = useCallback((value: string) => {
      setIsFiltering(value.length > 0);
      setOrganizeSearchText(value);
      setShowCreateCollection(value.length >= 2);
    }, []);

    const handleClearSearchCollections = () => {
      setOrganizeSearchText("");
      setShowCreateCollection(false);
    };

    const handleOrganizeIsOpenChange = useCallback(
      (isOpen: boolean) => {
        setOrganizeIsOpen(isOpen);
        if (!isOpen) {
          setOrganizeSearchText("");
          setShowCreateCollection(false);
        }
      },
      [setOrganizeIsOpen]
    );

    const handleAutoOrganize = useCallback(async () => {
      if (!noteId || !canAutoOrganize) return;
      setOrganizeIsOpen(false);
      await actions.autoOrganizeNotes({ noteIds: [noteId], store });
    }, [noteId, canAutoOrganize, store, setOrganizeIsOpen]);

    const handleSelectCollection = async (collectionId: string) => {
      if (!note) return;
      const collection = await store.collections.getAsync(collectionId);
      if (!collection) return;

      if (note.collectionList?.hasCollection({ collectionId: collection.id })) {
        await actions.removeNoteFromCollection({ note, collection, store });
      } else {
        await actions.addNotesToCollection({
          notes: [note],
          collection,
          store,
          eventContext: EventContext.EditorActions,
        });
      }
    };

    const handleCreateNewCollection = async (collectionName: string) => {
      if (!note) return;

      await actions.createCollection({
        title: collectionName,
        store,
        notes: [note],
        eventContext: EventContext.EditorActions,
      });

      setOrganizeSearchText("");
      setShowCreateCollection(false);
    };

    const handleEscape = useCallback(() => {
      if (isFiltering) handleSearchCollections("");
      else dropdownListRef.current?.focus();
    }, [isFiltering, handleSearchCollections]);

    // Determine which trigger to use
    const trigger = children ? (
      <div slot={MDSMenuDropdownSlotName.Trigger}>{children}</div>
    ) : note.collectionList?.allCollections.length === 0 ? (
      <MDSButton
        slot={MDSMenuDropdownSlotName.Trigger}
        variant={MDSButtonVariant.Subtle}
        size={MDSButtonSize.Small}
      >
        <MDSIcon kind={MDSIconKind.Collection} slot={MDSButtonSlotName.LeftIcon} />
        Organize
      </MDSButton>
    ) : (
      <StyledHashtagButton
        slot={MDSMenuDropdownSlotName.Trigger}
        iconKind={MDSIconKind.Collection}
      />
    );

    return (
      <MDSMenuDropdown isOpen={organizeIsOpen} onOpenChange={handleOrganizeIsOpenChange}>
        {trigger}

        <div slot={MDSMenuDropdownSlotName.Menu}>
          <MDSMenuInputHeader
            onChange={handleSearchCollections}
            onClear={handleClearSearchCollections}
            value={organizeSearchText}
            onEscape={handleEscape}
            onKeyDirection={() => dropdownListRef.current?.focus()}
            placeholder="Add to existing or new collection"
          />
          <MDSSelectMenu
            ref={dropdownListRef}
            aria-label="Organize note into collections"
            selectionBehavior="toggle"
            selectionMode="single"
            shouldFocusWrap={true}
            style={{ maxHeight: "75vh", maxWidth: 320 }}
          >
            {showAutoOrganize && (
              <>
                <MDSSelectMenuItem
                  label="Auto organize"
                  subtitle={autoOrganizeDisabledReason ?? "Let Mem organize into your collections"}
                  id="auto-organize"
                  icon={MDSIconKind.AutoOrganize}
                  isDisabled={!canAutoOrganize}
                  rightAddon={getShortcutMenuItemRightAddon({
                    action: "auto_organize_selected_notes",
                  })}
                  onClick={handleAutoOrganize}
                />
                <MDSMenuItemDivider />
              </>
            )}
            {organizeCollectionsHeader && <MDSMenuItemHeader label={organizeCollectionsHeader} />}
            {sortedCollections.map(collection => (
              <MDSSelectMenuItem
                key={collection.id}
                id={collection.id}
                label={collection.label}
                subtitle={collection.subtitle}
                icon={() => <MDSCollectionIcon size={11} collectionId={collection.id} />}
                onClick={() => handleSelectCollection(collection.id)}
                isSelected={note.collectionList?.hasCollection({ collectionId: collection.id })}
                selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
              />
            ))}
            {showCreateCollection && (
              <MDSSelectMenuItem
                id="new-collection"
                label={`"${organizeSearchText.trim()}"`}
                subtitle="Create new collection"
                icon={MDSIconKind.Plus}
                onClick={() => handleCreateNewCollection(organizeSearchText)}
                selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
              />
            )}
          </MDSSelectMenu>
        </div>
      </MDSMenuDropdown>
    );
  }
);

const StyledHashtagButton = styled(MDSIconButton)`
  margin-right: ${({ theme }) => theme.spacing[1]};
`;
